import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Copy, ExternalLink, RefreshCw } from "lucide-react";

const LIMIT_OPTIONS = [
  { value: 100, label: "100 products" },
  { value: 500, label: "500 products" },
  { value: 1000, label: "1,000 products" },
  { value: 5000, label: "5,000 products" },
  { value: 0, label: "All products" },
];

const fmtDate = (iso) => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export default function GmcFeedPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = () =>
    api
      .get("/admin/gmc/settings")
      .then((r) => setSettings(r.data))
      .catch(() => toast.error("Failed to load feed settings"));

  useEffect(() => {
    load();
  }, []);

  const update = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/admin/gmc/settings", patch);
      setSettings(r.data);
      toast.success("Feed settings updated");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const refreshTimestamp = async () => {
    if (!settings?.feed_url) return;
    setRefreshing(true);
    try {
      // Hitting the feed URL forces re-generation and updates last_generated_at
      await fetch(settings.feed_url, { cache: "no-store" });
      await load();
      toast.success("Feed regenerated");
    } catch {
      toast.error("Could not refresh feed");
    } finally {
      setRefreshing(false);
    }
  };

  const copyUrl = () => {
    if (!settings?.feed_url) return;
    navigator.clipboard.writeText(settings.feed_url);
    toast.success("Feed URL copied");
  };

  if (!settings) {
    return (
      <div className="mt-8 font-mono text-xs uppercase tracking-editorial text-[#1A1714]/60" data-testid="gmc-loading">
        Loading feed settings…
      </div>
    );
  }

  const limit = settings.limit ?? 0;

  return (
    <div className="mt-8 space-y-8 max-w-3xl" data-testid="gmc-panel">
      <header className="border-b border-[#E5E1D8] pb-4">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Integrations</p>
        <h2 className="font-serif text-3xl mt-1">Google Merchant Center</h2>
        <p className="text-sm text-[#1A1714]/70 mt-2 max-w-xl">
          Control the product feed submitted to Google Shopping and Google free listings. Disable the
          feed to prevent Google from fetching it. Adjust the product limit to start with a small
          test batch before publishing your full catalog.
        </p>
      </header>

      {/* Enable / disable */}
      <section className="border border-[#E5E1D8] p-6" data-testid="gmc-enable-section">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Feed status</p>
            <p className="font-serif text-xl mt-1">
              {settings.enabled ? "Enabled" : "Disabled"}
            </p>
            <p className="text-sm text-[#1A1714]/70 mt-2 max-w-md">
              {settings.enabled
                ? "Google can fetch the feed. Individual product listings are eligible for Shopping ads and free listings."
                : "The feed returns HTTP 503 with an empty channel. Google Merchant Center will pause fetches until you re-enable it."}
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => update({ enabled: v })}
            disabled={saving}
            data-testid="gmc-toggle"
          />
        </div>
      </section>

      {/* Product limit */}
      <section className="border border-[#E5E1D8] p-6" data-testid="gmc-limit-section">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Product limit</p>
        <p className="font-serif text-xl mt-1">
          {limit === 0 ? "All products" : limit.toLocaleString("en-US")}
        </p>
        <p className="text-sm text-[#1A1714]/70 mt-2 max-w-md">
          The feed will include at most this many products. Use a small batch (100 – 1,000) while
          testing your Merchant Center setup, then switch to "All products" once approved.
        </p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="gmc-limit-options">
          {LIMIT_OPTIONS.map((opt) => {
            const active = limit === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => update({ limit: opt.value })}
                disabled={saving}
                data-testid={`gmc-limit-${opt.value}`}
                className={
                  "px-4 py-2 rounded-none font-mono text-xs uppercase tracking-editorial border transition-colors " +
                  (active
                    ? "bg-[#1A1714] text-[#F9F6F0] border-[#1A1714]"
                    : "bg-transparent text-[#1A1714] border-[#E5E1D8] hover:border-[#1A1714]")
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Feed URL */}
      <section className="border border-[#E5E1D8] p-6" data-testid="gmc-url-section">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Feed URL</p>
        <p className="text-sm text-[#1A1714]/70 mt-2">
          Submit this URL in Google Merchant Center → Data sources → Add data source → Scheduled fetch.
        </p>
        <div className="mt-4 flex items-center gap-2 bg-white border border-[#E5E1D8] p-3">
          <code
            className="flex-1 font-mono text-xs text-[#1A1714] break-all"
            data-testid="gmc-feed-url"
          >
            {settings.feed_url}
          </code>
          <Button
            onClick={copyUrl}
            variant="outline"
            className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
            data-testid="gmc-copy-btn"
          >
            <Copy size={12} className="mr-1" /> Copy
          </Button>
          <a
            href={settings.feed_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gmc-open-btn"
          >
            <Button
              variant="outline"
              className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
            >
              <ExternalLink size={12} className="mr-1" /> Open
            </Button>
          </a>
        </div>

        {settings.sitemap_url && (
          <div className="mt-4 text-xs text-[#1A1714]/70">
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 mb-1">
              Sitemap (for Google Search Console)
            </p>
            <code className="font-mono break-all">{settings.sitemap_url}</code>
          </div>
        )}
      </section>

      {/* Last update */}
      <section className="border border-[#E5E1D8] p-6" data-testid="gmc-status-section">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">
              Last generated
            </p>
            <p className="font-serif text-xl mt-1" data-testid="gmc-last-generated">
              {fmtDate(settings.last_generated_at)}
            </p>
            <p className="text-sm text-[#1A1714]/70 mt-2">
              Items in most recent build:{" "}
              <span className="font-semibold text-[#1A1714]" data-testid="gmc-last-count">
                {(settings.last_item_count || 0).toLocaleString("en-US")}
              </span>
            </p>
            <p className="text-xs text-[#1A1714]/50 mt-3 max-w-md">
              The feed is regenerated live on every request, so product edits appear the next time
              Google fetches it (subject to a 30-minute CDN cache).
            </p>
          </div>
          <Button
            onClick={refreshTimestamp}
            disabled={refreshing || !settings.enabled}
            className="rounded-none bg-[#1A1714] hover:bg-[#7A2021] text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            data-testid="gmc-refresh-btn"
          >
            <RefreshCw size={12} className={"mr-1 " + (refreshing ? "animate-spin" : "")} />
            {refreshing ? "Refreshing…" : "Refresh now"}
          </Button>
        </div>
      </section>
    </div>
  );
}
